
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Toggle } from '@/components/ui/toggle';
import { Plus } from 'lucide-react';
import { DateRangePicker } from '@/components/financial-reports/date-range-picker';
import type { DateRange } from 'react-day-picker';
import { PaymentTable } from '@/components/payments/payment-table';
import { AddPaymentDialog } from '@/components/payments/add-payment-dialog';
import { useGym } from '@/contexts/gym-context';
import { useView } from '@/contexts/view-context';
import { PageHeader } from '@/components/shared/page-header';

export default function PaymentsPage() {
  const { t } = useTranslation();
  const [addPaymentOpen, setAddPaymentOpen] = useState(false);
  const { viewMode } = useView();

  const { selectedGym, selectGym } = useGym();

  useEffect(() => {
    if (viewMode === 'reception') {
      selectGym(selectedGym);
    }
  }, []);

  // Set default date range to today only
  const today = new Date();

  const [dateRange, setDateRange] = useState<DateRange>({
    from: today,
    to: today,
  });

  // Comparison state
  const [comparisonMode, setComparisonMode] = useState(false);
  const [comparisonDateRange, setComparisonDateRange] = useState<DateRange>({
    from: undefined,
    to: undefined,
  });

  // Format dates to DD-MM-YYYY
  const formatDateForAPI = (date: Date) => {
    return format(date, 'dd-MM-yyyy');
  };

  const startDate = dateRange?.from ? formatDateForAPI(dateRange.from) : '';
  const endDate = dateRange?.to ? formatDateForAPI(dateRange.to) : '';

  const comparisonStartDate = comparisonDateRange?.from
    ? formatDateForAPI(comparisonDateRange.from)
    : '';
  const comparisonEndDate = comparisonDateRange?.to
    ? formatDateForAPI(comparisonDateRange.to)
    : '';

  const handleComparisonModeChange = (enabled: boolean) => {
    setComparisonMode(enabled);
    if (!enabled) {
      setComparisonDateRange({ from: undefined, to: undefined });
    }
  };

  // Wrapper functions for DateRangePicker compatibility
  const handleDateRangeChange = (range: DateRange | undefined) => {
    setDateRange(range || { from: undefined, to: undefined });
  };

  const handleComparisonDateRangeChange = (range: DateRange | undefined) => {
    setComparisonDateRange(range || { from: undefined, to: undefined });
  };

  return (
    <div className="space-y-6">
      <PageHeader title={t('payments.title')}>
        <Button onClick={() => setAddPaymentOpen(true)} className="w-full sm:w-auto">
          <Plus className="h-4 w-4 mr-2" />
          {t('payments.addPayment')}
        </Button>
      </PageHeader>

      <div className="flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-end">
        <div>
          <label className="mb-2 block text-sm font-medium">
            {t('payments.selectedPeriod', 'Selected Period')}
          </label>
          <DateRangePicker
            dateRange={dateRange}
            setDateRange={handleDateRangeChange}
            placeholder={t('financial.selectDateRange')}
          />
        </div>

        <div className="flex items-center">
          <Toggle
            pressed={comparisonMode}
            onPressedChange={handleComparisonModeChange}
            aria-label="Toggle comparison mode"
          >
            {t('payments.enableComparison', 'Enable Comparison')}
          </Toggle>
        </div>

        {comparisonMode && (
          <div>
            <label className="mb-2 block text-sm font-medium">
              {t('payments.comparisonPeriod', 'Comparison Period')}
            </label>
            <DateRangePicker
              dateRange={comparisonDateRange}
              setDateRange={handleComparisonDateRangeChange}
              placeholder={t('payments.selectComparisonDateRange')}
            />
          </div>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t('payments.paymentsList')}</CardTitle>
        </CardHeader>
        <CardContent>
          {startDate && endDate && (
            <PaymentTable
              startDate={startDate}
              endDate={endDate}
              comparisonStartDate={comparisonStartDate}
              comparisonEndDate={comparisonEndDate}
              comparisonMode={comparisonMode}
            />
          )}
        </CardContent>
      </Card>

      <AddPaymentDialog
        open={addPaymentOpen}
        onOpenChange={setAddPaymentOpen}
      />
    </div>
  );
}
